// =============================================================================
// features/meu-whatsapp/MeuWhatsAppPage.tsx — O vendedor conecta o próprio
// aparelho (F39-S08).
//
// É a URL que a gestora manda para os vendedores. Cada um abre já logado, o
// sistema reconhece a sessão e serve o QR DELE. Não existe seletor de vendedor,
// não existe id na URL: o backend deriva o agente do JWT.
//
// A tela é escrita para o vendedor, não para o administrador — sem jargão, sem
// enum cru, sem "canal" ou "sessão".
//
// LGPD: o QR é credencial de pareamento; vive no componente e some ao sair.
// =============================================================================

import { WHATSAPP_MONITORING_POLICY_KEY } from '@elemento/shared-schemas';
import * as React from 'react';
import { Link, Navigate } from 'react-router-dom';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-store';
import { cn } from '../../lib/cn';
import { MonitoringBanner } from '../policies/MonitoringBanner';
import { PolicyAcceptanceGate } from '../policies/PolicyAcceptanceGate';

import { ExcludedContactsPanel } from './ExcludedContactsPanel';
import { PairingQrPanel } from './PairingQrPanel';
import { SyncPanel } from './SyncPanel';
import { useDesconectarMeuAparelho, useMeuPareamento } from './useMeuWhatsApp';
import { useSessionStatusRealtime } from './useSessionStatusRealtime';

/**
 * F51-S12 — D4: a marcação/desmarcação de contato pessoal é do DONO da
 * caixa, nunca de um gestor em nome de outro. Esconder o painel aqui é
 * cosmético; quem barra de verdade é a API (a rota deriva o canal da sessão,
 * nunca aceita `channelId` de fora).
 */
const EXCLUDED_CONTACTS_PERMISSION = 'channel.excluded_contacts:manage';

export function MeuWhatsAppPage(): React.JSX.Element {
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag(
    'whatsapp_sync.pairing.enabled',
  );
  const { hasPermission } = useAuth();
  const canManageExcludedContacts = hasPermission(EXCLUDED_CONTACTS_PERMISSION);

  // F43-S12: queda/retomada da sessão chega por socket — sem precisar
  // recarregar a página para o status atualizar.
  useSessionStatusRealtime();

  const me = useMeuPareamento();
  const desconectar = useDesconectarMeuAparelho();

  const status = me.data?.status ?? 'STOPPED';
  const conectado = status === 'WORKING';

  if (flagLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]" role="status">
        <div className="w-6 h-6 rounded-full border-2 border-border-strong border-t-azul animate-spin" />
      </div>
    );
  }

  if (!flagEnabled) return <Navigate to="/404" replace />;

  // Usuário sem agente vinculado — mensagem para o vendedor, não stack trace.
  if (me.isError && me.error instanceof ApiError && me.error.status === 404) {
    return (
      <div className="max-w-lg mx-auto py-12 text-center">
        <h1 className="font-display font-bold text-ink" style={{ fontSize: 'var(--text-2xl)' }}>
          Seu acesso ainda não está pronto
        </h1>
        <p className="font-sans text-sm text-ink-3 mt-2">{me.error.message}</p>
      </div>
    );
  }

  return (
    <PolicyAcceptanceGate policyKey={WHATSAPP_MONITORING_POLICY_KEY}>
      <div
        className="max-w-lg mx-auto flex flex-col gap-6 py-2"
        style={{ animation: 'fade-up var(--dur-slow) var(--ease-out)' }}
      >
        <MonitoringBanner />
        <div>
          <h1
            className="font-display font-bold text-ink leading-tight"
            style={{ fontSize: 'var(--text-3xl)', letterSpacing: '-0.04em' }}
          >
            Meu WhatsApp
          </h1>
          <p className="font-sans text-sm text-ink-3 mt-1">
            {me.data !== undefined
              ? `Olá, ${me.data.agent_name}. Conecte seu celular para que suas conversas apareçam aqui.`
              : 'Conecte seu celular para que suas conversas apareçam aqui.'}
          </p>
        </div>

        {conectado ? (
          <div
            className="rounded-md border border-success/30 bg-success-soft p-5 flex flex-col gap-3"
            style={{ boxShadow: 'var(--elev-1)' }}
          >
            <p className="font-sans text-sm font-semibold text-ink">Seu WhatsApp está conectado</p>
            {me.data?.phone_e164 !== null && me.data?.phone_e164 !== undefined ? (
              <p className="font-mono text-xs text-ink-3">{me.data.phone_e164}</p>
            ) : null}
            <SyncPanel sync={me.data?.sync ?? null} />
            {/* F43-S13: entrada para a caixa pessoal — o recorte de /conversas
              filtrado por este canal. */}
            <Link
              to="/meu-whatsapp/inbox"
              className={cn(
                'self-start font-sans text-sm font-semibold px-3 py-2 rounded-sm',
                'bg-azul text-white',
                'transition-[filter] duration-fast hover:brightness-110',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
              )}
            >
              Ver minhas conversas
            </Link>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Desconectar seu WhatsApp do sistema?')) desconectar.mutate();
              }}
              disabled={desconectar.isPending}
              className={cn(
                'self-start font-sans text-sm font-medium px-3 py-2 rounded-sm',
                'border border-danger/40 text-danger',
                'transition-colors duration-fast hover:bg-danger-soft',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              Desconectar
            </button>
          </div>
        ) : null}

        {conectado ? <ExcludedContactsPanel enabled={canManageExcludedContacts} /> : null}

        {/* F56-S03: o bloco do QR foi extraído para PairingQrPanel (compartilhado com o
            convite do AppLayout) — mesmo painel, mesmo comportamento. */}
        {!conectado ? (
          <PairingQrPanel
            status={status}
            hasChannel={me.data?.channel_id !== null && me.data?.channel_id !== undefined}
          />
        ) : null}
      </div>
    </PolicyAcceptanceGate>
  );
}
